import { Box, Card, SvgIcon } from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonWrapper } from '../formUtilities/useButton';
import { getToken, getUser } from '../../../services/authService';
import { API_KEY } from '../../../config/appConfig';

const LEAN_PENDING_PREFIX = 'fs_lean_connect_pending';

const LEAN_POLL_INTERVAL_MS = 2500;
const LEAN_POLL_TIMEOUT_MS = 10 * 60 * 1000;
const LEAN_POLL_GRACE_MS = 90_000;

const LEAN_REDIRECT_QUERY_KEYS = [
	'entity_id',
	'bank_identifier',
	'customer_id',
	'consent_attempt_id',
	'consent_id',
	'granular_status_code',
	'status_additional_info',
	'status',
	'error',
	'error_description',
];

const getPendingKey = (userId) => (userId ? `${LEAN_PENDING_PREFIX}:${userId}` : null);

const clearLegacyPendingKey = () => {
	try {
		sessionStorage.removeItem(LEAN_PENDING_PREFIX);
		localStorage.removeItem(LEAN_PENDING_PREFIX);
	} catch (_) { }
};

const setPendingRecord = (userId, startedAt = Date.now()) => {
	const key = getPendingKey(userId);
	if (!key) return startedAt;
	const payload = JSON.stringify({ pending: true, startedAt });
	try {
		sessionStorage.setItem(key, payload);
	} catch (_) { }
	try {
		localStorage.setItem(key, payload);
	} catch (_) { }
	return startedAt;
};

const clearPendingRecord = (userId) => {
	const key = getPendingKey(userId);
	if (!key) return;
	try {
		sessionStorage.removeItem(key);
	} catch (_) { }
	try {
		localStorage.removeItem(key);
	} catch (_) { }
};

const readPendingRecord = (userId) => {
	const key = getPendingKey(userId);
	if (!key) return { pending: false, startedAt: 0 };

	let raw = null;
	try {
		raw = sessionStorage.getItem(key) || localStorage.getItem(key);
	} catch (_) {
		raw = null;
	}

	if (!raw) return { pending: false, startedAt: 0 };

	if (raw === 'true') {
		const startedAt = setPendingRecord(userId, Date.now());
		return { pending: true, startedAt };
	}

	try {
		const obj = JSON.parse(raw);
		const pending = obj?.pending === true;
		const startedAt = Number(obj?.startedAt || 0);
		if (!pending) {
			clearPendingRecord(userId);
			return { pending: false, startedAt: 0 };
		}
		if (!startedAt || Number.isNaN(startedAt)) {
			return { pending: true, startedAt: Date.now() };
		}
		if (Date.now() - startedAt > LEAN_POLL_TIMEOUT_MS) {
			clearPendingRecord(userId);
			return { pending: false, startedAt: 0 };
		}
		return { pending: true, startedAt };
	} catch (_) {
		clearPendingRecord(userId);
		return { pending: false, startedAt: 0 };
	}
};

const readLeanRedirectStatus = () => {
	try {
		const url = new URL(window.location.href);
		const status = url.searchParams.get('status');
		let hadAny = false;
		LEAN_REDIRECT_QUERY_KEYS.forEach((k) => {
			if (url.searchParams.has(k)) {
				url.searchParams.delete(k);
				hadAny = true;
			}
		});
		if (hadAny) {
			const next = `${url.pathname}${url.search ? url.search : ''}${url.hash || ''}`;
			window.history.replaceState({}, document.title, next);
		}
		return { hadRedirect: hadAny, status: status?.toUpperCase() || '' };
	} catch (_) {
		return { hadRedirect: false, status: '' };
	}
};

const getIbanValue = (account) => String(account?.iban ?? account?.Iban ?? '').trim();
const getAccountNameValue = (account) => String(account?.name ?? account?.Name ?? '').trim();
const getAccounts = (bank) => bank?.Accounts || bank?.accounts || [];
const hasAnyIban = (banks) => (banks || []).some((b) => (getAccounts(b) || []).some((a) => !!getIbanValue(a)));
const bankHasIban = (bank) => (getAccounts(bank) || []).some((a) => !!getIbanValue(a));
const countIbans = (banks) => (banks || []).reduce((sum, b) => sum + (getAccounts(b) || []).filter((a) => !!getIbanValue(a)).length, 0);
const isBankConnecting = (bank) => !!(bank?.IsConnecting || bank?.isConnecting);

const BankBuildingIcon = (props) => (
	<SvgIcon {...props} viewBox="0 0 24 24">
		<path d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zm6 0v7h3v-7h-3zM2 20v2h20v-2H2zm2-9h16V9L12 3 4 9v2z" />
	</SvgIcon>
);

export const ConnectBankSection = ({ onConnectedChange = (e) => { } }) => {
	const { t, i18n } = useTranslation();
	const userId = getUser()?.nameid;

	const [loading, setLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(true);
	const [isFetching, setIsFetching] = useState(false);
	const [error, setError] = useState(null);
	const [banks, setBanks] = useState([]);

	const pollRef = useRef(null);
	const pollStartedAtRef = useRef(0);
	const pollInFlightRef = useRef(false);
	const loadInFlightRef = useRef(false);
	const ibanCountBeforeConnectRef = useRef(0);
	const banksRef = useRef([]);
	const connectIdRef = useRef(0);

	useEffect(() => {
		stopPolling();
		pollInFlightRef.current = false;
		loadInFlightRef.current = false;
		ibanCountBeforeConnectRef.current = 0;
		banksRef.current = [];
		setBanks([]);
		setError(null);
		setIsFetching(false);
		setInitialLoading(true);

		clearLegacyPendingKey();

		if (!userId) {
			setInitialLoading(false);
			return () => stopPolling();
		}

		const redirect = readLeanRedirectStatus();
		if (redirect.hadRedirect && redirect.status === 'SUCCESS') {
			const startedAt = setPendingRecord(userId, Date.now());
			setIsFetching(true);
			startPolling(startedAt);
			loadConnectedBanks(true);
			return () => stopPolling();
		}

		const pendingRecord = readPendingRecord(userId);
		if (pendingRecord.pending) {
			setIsFetching(true);
			startPolling(pendingRecord.startedAt || Date.now());
		}

		loadConnectedBanks(pendingRecord.pending);
		return () => stopPolling();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId]);

	useEffect(() => {
		onConnectedChange && onConnectedChange(hasAnyIban(banks));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [banks]);

	const API_BASE = String(API_KEY || '').replace(/['"]/g, '').trim().replace(/\/$/, '');

	const apiJson = async (method, url) => {
		const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
		const token = getToken();

		const resp = await fetch(fullUrl, {
			method,
			credentials: 'omit',
			cache: 'no-store',
			headers: {
				'Content-Type': 'application/json',
				'Cache-Control': 'no-cache, no-store, must-revalidate',
				Pragma: 'no-cache',
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
		});

		const contentType = resp.headers.get('content-type') || '';
		const text = await resp.text();

		let data = null;
		if (text && contentType.includes('application/json')) {
			try { data = JSON.parse(text); } catch (_) { }
		}

		if (!resp.ok) {
			const msg =
				(data && (data.message || data.error)) ||
				(!contentType.includes('application/json') && text ? text.slice(0, 200) : null) ||
				`Request failed: ${method} ${fullUrl} (${resp.status})`;
			throw new Error(msg);
		}

		return data;
	};

	const stopPolling = () => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	};

	const doPollTick = async () => {
		const elapsed = Date.now() - pollStartedAtRef.current;
		if (elapsed > LEAN_POLL_TIMEOUT_MS) {
			stopPolling();
			clearPendingRecord(userId);
			setIsFetching(false);
			setError(
				t(
					'bankConnectionTimeout',
					'Bank connection is taking longer than expected. Please try again shortly.'
				)
			);
			return;
		}

		if (pollInFlightRef.current) return;
		pollInFlightRef.current = true;
		try {
			await loadConnectedBanks(true);
		} finally {
			pollInFlightRef.current = false;
		}
	};

	const startPolling = (startedAt) => {
		pollStartedAtRef.current = startedAt || Date.now();
		if (pollRef.current) return;
		pollRef.current = setInterval(doPollTick, LEAN_POLL_INTERVAL_MS);
	};

	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible' && pollRef.current) {
				doPollTick();
			}
		};
		document.addEventListener('visibilitychange', onVisibilityChange);
		return () => document.removeEventListener('visibilitychange', onVisibilityChange);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const normalizeHex = (value, fallback) => {
		if (!value) return fallback;
		const v = String(value).trim();
		if (!v) return fallback;
		if (v.startsWith('#')) return v;
		if (/^[0-9A-Fa-f]{6}$/.test(v)) return `#${v}`;
		return v;
	};

	const toTitleCase = (value) => {
		const v = String(value || '').replace(/_/g, ' ').trim();
		if (!v) return v;
		return v
			.split(' ')
			.filter(Boolean)
			.map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
			.join(' ');
	};

	const mergeBanks = (list) => {
		const map = new Map();
		(list || []).forEach((b) => {
			const identifier = b?.identifier || b?.Identifier || b?.bank_identifier || b?.BankIdentifier;
			const key = identifier || JSON.stringify(b);
			if (!map.has(key)) {
				map.set(key, { ...b, Accounts: b?.Accounts || b?.accounts || [] });
			} else {
				const existing = map.get(key);
				const acc = [...(existing?.Accounts || []), ...(b?.Accounts || b?.accounts || [])];
				const seen = new Set();
				const merged = [];
				acc.forEach((a) => {
					const id = a?.Id || a?.id || '';
					const iban = a?.Iban || a?.iban || '';
					const k = id || iban || JSON.stringify(a);
					if (seen.has(k)) return;
					seen.add(k);
					merged.push(a);
				});
				map.set(key, { ...existing, ...b, Accounts: merged });
			}
		});
		return Array.from(map.values());
	};

	const mapEntitiesToBanks = (entities) => {
		return (entities || []).map((e) => {
			const bd = e?.BankDetails || e?.bankDetails || {};
			return {
				Identifier: bd?.Identifier,
				BankType: bd?.BankType,
				Name: bd?.Name,
				AccountType: bd?.AccountType,
				Logo: bd?.Logo,
				MainColor: bd?.MainColor,
				BackgroundColor: bd?.BackgroundColor,
				Accounts: bd?.Accounts || [],
			};
		});
	};

	const loadConnectedBanks = async (fromPending = false) => {
		if (loadInFlightRef.current) return;
		loadInFlightRef.current = true;
		try {
			if (!userId) {
				setInitialLoading(false);
				return;
			}

			const resp = await apiJson('GET', `/api/Borrowers/Me/ConnectedBanks?t=${Date.now()}`);
			let list = resp?.banks || resp?.Banks || [];
			if (!list?.length && (resp?.Entities || resp?.entities)) {
				list = mapEntitiesToBanks(resp?.Entities || resp?.entities);
			}
			list = mergeBanks(list);

			banksRef.current = list;
			setBanks(list);

			const anyConnecting = (list || []).some(isBankConnecting);
			const apiPending = resp?.Pending ?? resp?.pending ?? false;
			const currentIbanCount = countIbans(list);
			const elapsed = pollStartedAtRef.current ? Date.now() - pollStartedAtRef.current : 0;
			const pastGrace = elapsed > LEAN_POLL_GRACE_MS;
			if (fromPending && ibanCountBeforeConnectRef.current === 0 && currentIbanCount > 0 && (anyConnecting || apiPending)) { ibanCountBeforeConnectRef.current = currentIbanCount; }
			const newDataAppeared = currentIbanCount > ibanCountBeforeConnectRef.current;

			if (!fromPending) {
				if (anyConnecting || apiPending) {
					ibanCountBeforeConnectRef.current = currentIbanCount;
					const rec = readPendingRecord(userId);
					const startedAt = rec.pending ? rec.startedAt : setPendingRecord(userId, Date.now());
					setIsFetching(true);
					startPolling(startedAt);
				}
				return;
			}

			if (!anyConnecting && !apiPending) {
				if (newDataAppeared || (pastGrace && list.length > 0)) {
					clearPendingRecord(userId);
					setIsFetching(false);
					setError(null);
					stopPolling();
					const newlyConnected = (list || []).filter(b => !(b?.Confirmed ?? b?.confirmed) && bankHasIban(b));
					newlyConnected.forEach(b => {
						const identifier = b?.Identifier || b?.identifier;
						if (identifier) {
							apiJson('POST', `/api/Borrowers/${userId}/BankStatementLinkingConfirmation/Confirm?bankIdentifier=${encodeURIComponent(identifier)}`).catch(() => {});
						}
					});
					return;
				}
			}

			const rec = readPendingRecord(userId);
			const startedAt = rec.pending ? rec.startedAt : setPendingRecord(userId, Date.now());
			setIsFetching(true);
			startPolling(startedAt);
		} catch (e) {
			if (userId && (fromPending || readPendingRecord(userId).pending)) {
				setIsFetching(true);
				const rec = readPendingRecord(userId);
				startPolling(rec.startedAt || Date.now());
				return;
			}

			setError((e && e.message) || t('failedToLoadBankStatus', 'Failed to load bank status.'));
		} finally {
			loadInFlightRef.current = false;
			setInitialLoading(false);
		}
	};

	const getLeanCustomerSnapshot = async () => {
		if (!userId) throw new Error('Borrower id is missing (user.nameid is null)');
		const resp = await apiJson('GET', `/api/Borrowers/${userId}/LeanCustomer`);
		return {
			customerId: resp?.customerId ?? null,
			accessToken: resp?.accessToken ?? null,
			appToken: resp?.appToken ?? null,
			sandbox: resp?.sandbox ?? false,
		};
	};

	const getLeanCustomization = () => {
		const brand = '#33cd87';
		return {
			dialog_mode: 'contained',
			theme_color: brand,
			button_text_color: '#ffffff',
			button_border_radius: '8',
			overlay_color: 'rgba(0, 0, 0, 0.55)',
		};
	};

	const startConnect = async () => {
		if (!userId) return;

		const thisConnectId = ++connectIdRef.current;

		setError(null);
		setLoading(true);
		setIsFetching(true);

		try {
			const Lean = window.Lean;
			const connect = Lean?.connect || Lean?.Connect;
			if (typeof connect !== 'function') throw new Error('Lean SDK connect() is not available (window.Lean not ready)');

			const { customerId, accessToken, appToken, sandbox } = await getLeanCustomerSnapshot();
			if (!customerId) throw new Error('Lean customer_id is missing from API response. Please contact support.');
			if (!accessToken) throw new Error('Lean access_token is missing from API response. Please contact support.');
			if (!appToken) throw new Error('Lean app_token is missing from API response. Please contact support.');

			const now = Date.now();
			const accessFrom = new Date(now - 365 * 24 * 60 * 60 * 1000).toISOString();
			const accessTo = new Date(now + 364 * 24 * 60 * 60 * 1000).toISOString();
			const redirectUrl = (() => {
				try {
					const url = new URL(window.location.href);
					LEAN_REDIRECT_QUERY_KEYS.forEach((k) => url.searchParams.delete(k));
					return `${url.origin}${url.pathname}${url.search ? url.search : ''}`;
				} catch (_) {
					return window.location.href;
				}
			})();

			ibanCountBeforeConnectRef.current = countIbans(banksRef.current);
			const startedAt = setPendingRecord(userId, Date.now());
			apiJson('POST', `/api/Borrowers/${userId}/BankConnectionStarted`).catch(() => {});
			startPolling(startedAt);

			connect({
				app_token: appToken,
				access_token: accessToken,
				customer_id: customerId,
				permissions: ['identity', 'accounts', 'balance', 'transactions'],
				sandbox,
				language: (i18n.language || 'en').startsWith('ar') ? 'ar' : 'en',
				account_type: 'BUSINESS',
				success_redirect_url: redirectUrl,
				fail_redirect_url: redirectUrl,
				access_from: accessFrom,
				access_to: accessTo,
				customization: getLeanCustomization(),
				callback: (payload) => {
					if (connectIdRef.current !== thisConnectId) return;
					console.info('[Lean] callback', {
						status: payload?.status,
						secondary_status: payload?.secondary_status,
						exit_point: payload?.exit_point,
					});
					if (payload?.status === 'SUCCESS') {
						loadConnectedBanks(true);
					} else if (payload?.status === 'ERROR') {
						const othersConnecting = (banksRef.current || []).some(isBankConnecting);
						setError(payload?.message || 'Something went wrong while connecting the bank.');
						if (!othersConnecting) {
							clearPendingRecord(userId);
							setIsFetching(false);
							stopPolling();
						}
					} else if (payload?.status === 'CANCELLED') {
						const othersConnecting = (banksRef.current || []).some(isBankConnecting);
						if (!othersConnecting) {
							clearPendingRecord(userId);
							setIsFetching(false);
							stopPolling();
						}
					}
				},
			});

			setLoading(false);
		} catch (e) {
			if (connectIdRef.current !== thisConnectId) return;
			setError((e && e.message) || 'Failed to start Lean connect');
			clearPendingRecord(userId);
			setIsFetching(false);
			stopPolling();
			setLoading(false);
		}
	};

	const isConnected = useMemo(() => hasAnyIban(banks), [banks]);
	const hasBanks = isConnected;

	const statusLine = () => {
		if (error) {
			return (
				<Box className="flex items-center mt-2">
					<ErrorOutlineOutlinedIcon className="!text-sm !text-red-400" />
					<small className="caption mx-1 !text-red-400">{error}</small>
				</Box>
			);
		}

		if (initialLoading) {
			return (
				<Box className="flex items-start mt-2">
					<HourglassBottomOutlinedIcon className="!text-sm mt-1 !text-gray-500" />
					<small className="caption mx-1">{t('loadingBankStatus', 'Loading bank status...')}</small>
				</Box>
			);
		}

		if (isFetching) {
			return (
				<Box className="flex items-start mt-2">
					<HourglassBottomOutlinedIcon className="!text-sm mt-1 !text-gray-500" />
					<small className="caption mx-1">{t('connectingBank', 'Connecting...')}</small>
				</Box>
			);
		}

		if (!hasBanks && !isFetching) {
			return (
				<Box className="flex items-start mt-2">
					<BankBuildingIcon className="!text-sm mt-1 !text-gray-400" />
					<small className="caption mx-1">{t('notConnected', 'Not connected')}</small>
				</Box>
			);
		}

		return null;
	};

	const connectedPill = () => (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: '6px',
				padding: '2px 8px',
				borderRadius: '999px',
				background: 'rgba(51, 205, 135, 0.12)',
				border: '1px solid rgba(51, 205, 135, 0.35)',
			}}
		>
			<CheckCircleIcon sx={{ fontSize: 16, color: '#33cd87' }} />
			<Box className="font-[500] !text-sm" sx={{ color: '#1f8f58' }}>{t('connected', 'Connected')}</Box>
		</Box>
	);


	const bankTile = (bank, idx) => {
		const logo = bank?.logo || bank?.Logo;
		const name = bank?.name || bank?.Name;
		const identifier = bank?.identifier || bank?.Identifier;
		const mainColor = normalizeHex(bank?.mainColor || bank?.MainColor, '#33cd87');
		const backgroundColor = normalizeHex(bank?.backgroundColor || bank?.BackgroundColor, '#ffffff');
		const tileBg = (logo && String(logo).toLowerCase().includes('white') && String(backgroundColor).toLowerCase() === '#ffffff') ? mainColor : backgroundColor;

		const accounts = getAccounts(bank) || [];
		const accountsWithIban = (accounts || []).filter((a) => !!getIbanValue(a));
		const showConnected = !!accountsWithIban?.length;

		return (
			<Box
				key={`${identifier || idx}`}
				className="flex items-start mt-3"
				sx={{
					width: '117%',
					boxSizing: 'border-box',
					padding: '12px',
					borderRadius: '10px',
					border: '1px solid rgba(0,0,0,0.06)',
					background: '#ffffff',
				}}
			>
				<Box
					sx={{
						width: 44,
						height: 44,
						borderRadius: 2,
						overflow: 'hidden',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						background: tileBg,
						border: `2px solid ${mainColor}`,
						flex: '0 0 auto',
					}}
				>
					{logo ? (
						<img
							src={logo}
							alt={name || identifier || 'bank'}
							style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 6 }}
						/>
					) : (
						<BankBuildingIcon sx={{ color: mainColor }} />
					)}
				</Box>

				<Box className="mx-2 w-full">
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'minmax(180px, 42%) 1fr',
							columnGap: '20px',
							alignItems: 'center',
						}}
					>
						<Box className="file-name-text font-[600] !text-sm">
							{name || identifier}
						</Box>
						{showConnected ? (
							<Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
								{connectedPill()}
							</Box>
						) : null}
					</Box>

					{accountsWithIban && accountsWithIban.length ? (
						<Box
							sx={{
								marginTop: '14px',
								display: 'grid',
								gridTemplateColumns: 'minmax(180px, 42%) 1fr',
								columnGap: '20px',
								rowGap: '8px',
							}}
						>
							{accountsWithIban.map((a, i) => {
								const accName = getAccountNameValue(a);
								const iban = getIbanValue(a);

								if (!accName && !iban) return null;

								return (
									<Fragment key={`${a?.id || a?.Id || i}`}>
										<Box>
											{accName ? <Box className="font-[500] !text-sm">{toTitleCase(accName)}</Box> : null}
										</Box>

										<Box>
											<Box className="font-[500] !text-sm">
												{iban ? (
													<>
														{t('iban', 'IBAN')}: {iban}
													</>
												) : null}
											</Box>
										</Box>
									</Fragment>
								);
							})}
						</Box>
					) : null}
				</Box>
			</Box>
		);
	};

	const banksList = () => {
		if (!banks?.length) return null;
		return <Box className="pt-1">{banks.map((b, i) => bankTile(b, i))}</Box>;
	};

	return (
		<Box className="p-3" component={Card}>
			<Box className="border border-dashed relative flex justify-between items-start p-3 rounded-md">
				<Box className="flex flex-0 items-start w-full">
					<Box>
						<ButtonWrapper
							disabled
							classList="!bg-[#f7f7f7] !text-primary"
							iconButton
							icon={<BankBuildingIcon />}
						/>
					</Box>

					<Box className="mx-3 font-[500] !text-sm w-full">
						<Box>{t('connectBankTitle', 'Connect Bank')}</Box>
						<small className="caption">
							{t('connectBankSubtitle', 'View-only access via secure open banking, compliant with Saudi Central Bank regulations.')}
						</small>
						{statusLine()}
						{banksList()}
					</Box>
				</Box>

				<Box className="flex flex-row items-start justify-end">
					<ButtonWrapper
						classList="primary-bg !max-w-[180px]"
						loading={loading}
						disabled={loading || initialLoading}
						label={t('connectBankButton', 'Connect bank')}
						onClick={startConnect}
					/>
				</Box>
			</Box>
		</Box>
	);
};